/**
 * Light or dark, remembered across visits.
 *
 * Dark is the default because dark is what this UI has always looked like — not
 * the system preference. Following the OS would silently change the product's
 * appearance for anyone whose laptop is set to light, which is a redesign
 * disguised as a feature; a stored choice is the only thing that changes it.
 *
 * The theme is one class on `<html>`, and nothing else. No context, no provider,
 * no state threaded through components: the colours resolve through CSS variables
 * (`src/index.css`), so the class is the whole mechanism and this hook only has to
 * keep React's copy of it in step for the button's label.
 *
 * `index.html` sets the same class from the same key before the bundle loads, and
 * has to, or every light-theme visit paints dark first. That is a duplicated
 * literal — it cannot import anything — so `tests/test_ui_theme.py` asserts the
 * two agree.
 */
import { useCallback, useState } from 'react'

export type Theme = 'dark' | 'light'

/** Must match the key the pre-paint script in index.html reads. */
export const THEME_STORAGE_KEY = 'td-theme'

/** Must match the class the pre-paint script in index.html sets. */
export const LIGHT_CLASS = 'light'

/**
 * Read the theme off the document rather than off storage: the pre-paint script
 * has already resolved storage, and a second read could disagree with what is on
 * screen if storage is unreadable.
 */
function current(): Theme {
  return document.documentElement.classList.contains(LIGHT_CLASS) ? 'light' : 'dark'
}

export function useTheme(): { theme: Theme; toggle: () => void } {
  const [theme, setTheme] = useState<Theme>(current)

  const toggle = useCallback(() => {
    const next: Theme = current() === 'light' ? 'dark' : 'light'
    document.documentElement.classList.toggle(LIGHT_CLASS, next === 'light')
    // Persisting is best-effort: localStorage throws in some privacy modes, and
    // losing the preference for this tab is a better outcome than a toggle that
    // appears to do nothing because it threw before repainting.
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next)
    } catch {
      /* ignore */
    }
    setTheme(next)
  }, [])

  return { theme, toggle }
}
